import { CountryMasterPgsFilterPair, FeatureOption } from '@utils/pageConfig/Types';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getSessionStorage, setSessionStorage, StorageKey } from '@utils/StorageUtils';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import { EMPTY_FEATURE_OPTION } from '@utils/hook/selector/ShowPerformanceWithinSelector';

export interface PgAvailabilityState {
  selectedCountryPgAvailability: CountryMasterPgsFilterPair[];
  currentSelectedPeriodicity: Periodicity;
  periodLabel: string;
  totalAvailablePgs: number;
  totalUnavailablePgs: number;
  totalSubscriptionPgs: number;
  unavailablePgs: string[];
  isMultiCountry?: boolean;
  feature?: FeatureOption;
}

export type LoadedProductGroupsState = Pick<PgAvailabilityState, 'totalAvailablePgs' | 'totalSubscriptionPgs'>;

const initialState = (): PgAvailabilityState => {
  const storedAvailablePgs = JSON.parse(
    getSessionStorage(StorageKey.PgAvailabilityBasedSingleCountryAndPeriods) as string,
  );

  if (storedAvailablePgs !== null) {
    const {
      selectedCountryPgAvailability,
      currentSelectedPeriodicity,
      periodLabel,
      totalAvailablePgs,
      totalUnavailablePgs,
      totalSubscriptionPgs,
      unavailablePgs,
      isMultiCountry,
      feature,
    } = storedAvailablePgs;

    return {
      selectedCountryPgAvailability: selectedCountryPgAvailability,
      currentSelectedPeriodicity: currentSelectedPeriodicity,
      periodLabel: periodLabel,
      totalAvailablePgs: totalAvailablePgs,
      totalUnavailablePgs: totalUnavailablePgs,
      totalSubscriptionPgs: totalSubscriptionPgs,
      unavailablePgs: unavailablePgs,
      isMultiCountry: isMultiCountry,
      feature: feature,
    };
  }

  return {
    selectedCountryPgAvailability: [],
    currentSelectedPeriodicity: Periodicity.Monthly,
    periodLabel: '',
    totalAvailablePgs: 0,
    totalUnavailablePgs: 0,
    totalSubscriptionPgs: 0,
    unavailablePgs: [],
    isMultiCountry: false,
    feature: EMPTY_FEATURE_OPTION,
  };
};

const availabilityPgSingleCountrySlice = createSlice({
  name: 'availabilityPgSingleCountry',
  initialState,
  reducers: {
    pgsAvailabilityBasedPeriodAndCountry: {
      reducer: (state, action: PayloadAction<PgAvailabilityState>) => {
        Object.assign(state, action.payload);
      },
      prepare: (pgsAvailable) => {
        setSessionStorage(StorageKey.PgAvailabilityBasedSingleCountryAndPeriods, JSON.stringify(pgsAvailable));
        return {
          payload: pgsAvailable,
        };
      },
    },
  },
});

export const { pgsAvailabilityBasedPeriodAndCountry } = availabilityPgSingleCountrySlice.actions;

export default availabilityPgSingleCountrySlice.reducer;
